import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, forkJoin, of, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BoardService } from '../../../services/board.service';
import { NotificationService } from '../../../services/notifications.service';
import { environment } from '../../../../environments/environment';
import { DEFAULT_PROFILE_ICON } from '../../../enum/constants';
import { AddressInput, getCardPhoneValue, ICardEmail, ICardPhone, ICardV2 } from '../../../interfaces/card';
import { IColumn, IUser } from '../../../interfaces';
import { Tag } from '../../tag/tag.component';
import { BoardColumnColor, BoardField, MergeCardsPayload } from '../board-v3.types';
import { formatMoneyAmount, formatPriceFieldValue, isPriceFieldValue } from '../price-field.utils';
import { formatFullName } from '../../../helpers';
import { formatAddress, toAddressInput, uniqueAddresses } from '../address.utils';

type MergeSelectionKind = 'single' | 'multi';
type MergeValueRender = 'text' | 'tag' | 'user' | 'list';

interface MergeUser {
  name: string;
  avatarUrl: string;
}

interface MergeValue {
  cardId: string;
  raw: unknown;
  text: string;
  lines: string[];
  isEmpty: boolean;
  tag: Tag | null;
  user: MergeUser | null;
}

interface MergeField {
  key: string;
  title: string;
  translate: boolean;
  kind: MergeSelectionKind;
  render: MergeValueRender;
  values: Record<string, MergeValue>;
}

interface MergeGroup {
  key: string;
  title: string;
  translate: boolean;
  fields: MergeField[];
  collapsed: boolean;
}

interface SystemFieldDefinition {
  key: string;
  title: string;
  kind: MergeSelectionKind;
  render: MergeValueRender;
}

const SYSTEM_FIELDS: SystemFieldDefinition[] = [
  { key: 'name', title: 'custom_filters.name', kind: 'single', render: 'text' },
  { key: 'phase', title: 'phase', kind: 'single', render: 'tag' },
  { key: 'price', title: 'custom_filters.price', kind: 'single', render: 'text' },
  { key: 'assigned_id', title: 'custom_filters.assigned_id', kind: 'single', render: 'user' },
  { key: 'phone', title: 'phone', kind: 'multi', render: 'list' },
  { key: 'email', title: 'email', kind: 'multi', render: 'list' },
  { key: 'addresses', title: 'addresses', kind: 'multi', render: 'list' },
  { key: 'description', title: 'description', kind: 'multi', render: 'text' },
];

/** Merge field key -> key expected in the merge request `fields` object. */
const PAYLOAD_FIELD_KEYS: Record<string, string> = {
  name: 'name',
  phase: 'column_id',
  price: 'price',
  assigned_id: 'assigned_id',
  phone: 'phones',
  email: 'emails',
  addresses: 'addresses',
  description: 'description',
};

const SYSTEM_FIELD_KEYS = new Set([
  ...SYSTEM_FIELDS.map((field) => field.key),
  'currency',
  'created_at',
  'updated_at',
  'updated_by',
]);

@Component({
  selector: 'app-board-v3-merge-cards',
  templateUrl: './merge-cards.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'block h-full min-h-0' },
})
export class BoardV3MergeCardsComponent implements OnChanges {
  private readonly boardService = inject(BoardService);
  private readonly translateService = inject(TranslateService);
  private readonly notificationService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() cardIds: string[] = [];

  @Output() close = new EventEmitter<MergeCardsPayload | void>();

  cards: ICardV2[] = [];
  groups: MergeGroup[] = [];
  isLoading = false;
  isMerging = false;
  loadError = false;
  primaryCardId: string | null = null;

  private fieldTitles = new Map<string, string>();
  private singleSelection: Record<string, string> = {};
  private multiSelection: Record<string, Record<string, boolean>> = {};
  private results: Record<string, MergeValue | null> = {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cardIds']) this.loadCards();
  }

  get hasEnoughCards(): boolean {
    return this.uniqueCardIds.length > 1;
  }

  get canMerge(): boolean {
    return !this.isLoading && !this.isMerging && !this.loadError && this.cards.length > 1 && !!this.primaryCardId;
  }

  /**
   * Grid columns: every card column is followed by a narrow operator column ("+" / "="),
   * the last one belongs to the result column.
   */
  get gridTemplateColumns(): string {
    const cardColumns = this.cards.map(() => 'minmax(240px, 1fr) 32px').join(' ');

    return `${cardColumns} minmax(240px, 1fr)`;
  }

  cardLabel(index: number): string {
    return this.translate('board_v3_merge_cards_card_number', { index: index + 1 });
  }

  operatorFor(index: number): string {
    return index === this.cards.length - 1 ? '=' : '+';
  }

  isPrimary(cardId: string): boolean {
    return this.primaryCardId === cardId;
  }

  selectPrimary(cardId: string): void {
    this.primaryCardId = cardId;
    this.groups.forEach((group) =>
      group.fields.forEach((field) => {
        if (field.kind === 'single') {
          this.singleSelection[field.key] = this.resolveSingleSource(field, cardId);
          return;
        }

        this.multiSelection[field.key] = this.cards.reduce<Record<string, boolean>>((acc, card) => {
          acc[card.id] = !field.values[card.id]?.isEmpty;

          return acc;
        }, {});
      }),
    );
    this.recomputeResults();
  }

  valueFor(field: MergeField, cardId: string): MergeValue | null {
    return field.values[cardId] ?? null;
  }

  resultFor(field: MergeField): MergeValue | null {
    return this.results[field.key] ?? null;
  }

  selectedSourceFor(field: MergeField): string {
    return this.singleSelection[field.key] ?? '';
  }

  isSelected(field: MergeField, cardId: string): boolean {
    if (field.kind === 'single') return this.singleSelection[field.key] === cardId;

    return !!this.multiSelection[field.key]?.[cardId];
  }

  onCellClick(field: MergeField, cardId: string): void {
    if (field.kind !== 'single' || field.values[cardId]?.isEmpty) return;

    this.selectSingleValue(field, cardId);
  }

  selectSingleValue(field: MergeField, cardId: string): void {
    this.singleSelection[field.key] = cardId;
    this.recomputeField(field);
  }

  toggleMultiValue(field: MergeField, cardId: string, checked: boolean): void {
    const selection = this.multiSelection[field.key] ?? {};
    selection[cardId] = checked;
    this.multiSelection[field.key] = selection;
    this.recomputeField(field);
  }

  toggleGroup(group: MergeGroup): void {
    group.collapsed = !group.collapsed;
  }

  confirm(): void {
    if (!this.canMerge || !this.primaryCardId) return;

    const payload = this.buildMergePayload(this.primaryCardId);

    this.isMerging = true;
    this.boardService
      .mergeCards(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.isMerging = false)),
      )
      .subscribe({
        next: () => this.close.emit(payload),
        error: () => this.notificationService.showError('', this.translate('board_v3_merge_cards_failed')),
      });
  }

  private buildMergePayload(targetCardId: string): MergeCardsPayload {
    const fields: Record<string, unknown> = {};
    const customFields: Record<string, unknown> = {};

    this.groups.forEach((group) =>
      group.fields.forEach((field) => {
        const sourceCardIds =
          field.kind === 'single'
            ? [this.singleSelection[field.key]].filter(Boolean)
            : this.cards.map((card) => card.id).filter((cardId) => this.isSelected(field, cardId));
        if (!sourceCardIds.length) return;

        const value = this.rawValueFor(field, sourceCardIds);
        const payloadKey = PAYLOAD_FIELD_KEYS[field.key];
        if (payloadKey) {
          fields[payloadKey] = payloadKey === 'addresses' ? uniqueAddresses(value as AddressInput[]) : value;
          return;
        }

        customFields[field.key] = value;
      }),
    );

    if (Object.keys(customFields).length) fields['custom_fields'] = customFields;

    return {
      ids: this.cards.map((card) => card.id),
      target_id: targetCardId,
      fields,
    };
  }

  cancel(): void {
    if (this.isMerging) return;

    this.close.emit();
  }

  private get uniqueCardIds(): string[] {
    return [...new Set((this.cardIds ?? []).filter(Boolean))];
  }

  private loadCards(): void {
    const ids = this.uniqueCardIds;
    this.cards = [];
    this.groups = [];
    this.results = {};
    this.singleSelection = {};
    this.multiSelection = {};
    this.primaryCardId = null;
    this.loadError = false;
    if (ids.length < 2) return;

    this.isLoading = true;
    forkJoin(ids.map((id) => this.boardService.getCardDetails(id).pipe(catchError(() => of(null)))))
      .pipe(
        switchMap((cards) => {
          const loaded = cards.filter((card): card is ICardV2 => !!card);
          if (loaded.length < 2) return of({ cards: loaded, fields: [] as BoardField[] });

          return forkJoin({
            cards: of(loaded),
            fields: this.boardService.getBoardFields(loaded[0].funnel_id).pipe(catchError(() => of([]))),
          });
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: ({ cards, fields }) => {
          if (cards.length < 2) {
            this.loadError = true;
            return;
          }

          this.cards = cards;
          this.fieldTitles = new Map(
            (fields ?? []).filter((field) => !!field.identifier).map((field) => [field.identifier, field.name]),
          );
          this.buildGroups();
          this.selectPrimary(cards[0].id);
        },
        error: () => (this.loadError = true),
      });
  }

  /**
   * Two blocks only: the system fields and, when the cards really differ, their custom fields.
   */
  private buildGroups(): void {
    const groups: MergeGroup[] = [];

    const mainFields = SYSTEM_FIELDS.map((definition) => this.buildSystemField(definition)).filter(
      (field): field is MergeField => !!field,
    );
    if (mainFields.length) {
      groups.push({
        key: 'main',
        title: 'board_v3_merge_cards_main_fields',
        translate: true,
        fields: mainFields,
        collapsed: false,
      });
    }

    const customFields = this.collectCustomKeys()
      .filter((key) => !SYSTEM_FIELD_KEYS.has(key))
      .map((key) => this.buildCustomField(key, this.fieldTitles.get(key) ?? key, false))
      .filter((field): field is MergeField => !!field)
      .filter((field) => this.hasDifferentValues(field));
    if (customFields.length) {
      groups.push({
        key: 'custom',
        title: 'board_v3_merge_cards_custom_fields',
        translate: true,
        fields: customFields,
        collapsed: false,
      });
    }

    this.groups = groups;
  }

  private hasDifferentValues(field: MergeField): boolean {
    const texts = new Set(this.cards.map((card) => field.values[card.id]?.text ?? ''));

    return texts.size > 1;
  }

  private buildSystemField(definition: SystemFieldDefinition): MergeField | null {
    const values = this.cards.reduce<Record<string, MergeValue>>((acc, card) => {
      acc[card.id] = this.buildSystemValue(card, definition);

      return acc;
    }, {});
    const isAlwaysVisible = definition.key === 'name' || definition.key === 'phase';
    if (!isAlwaysVisible && Object.values(values).every((value) => value.isEmpty)) return null;

    return { ...definition, values, translate: true };
  }

  private buildSystemValue(card: ICardV2, definition: SystemFieldDefinition): MergeValue {
    switch (definition.key) {
      case 'phase':
        return this.createValue(card.id, card.column?.id ?? null, card.column?.name ?? '', {
          tag: card.column?.name
            ? {
                id: card.column.id,
                name: card.column.name,
                color: this.columnColor(card.column),
              }
            : null,
        });
      case 'price':
        return this.createValue(card.id, card.price, card.price ? formatMoneyAmount(card.price, card.currency) : '');
      case 'assigned_id':
        return this.createValue(card.id, card.assigned_id ?? null, this.managerName(card.assigned), {
          user: card.assigned
            ? { name: this.managerName(card.assigned), avatarUrl: this.managerAvatarUrl(card.assigned) }
            : null,
        });
      case 'phone': {
        const phones = (card.phones ?? []).map((phone: ICardPhone) => getCardPhoneValue(phone)).filter(Boolean);

        return this.createValue(card.id, phones, phones.join('\n'), { lines: phones });
      }
      case 'email': {
        const emails = (card.emails ?? []).map((email: ICardEmail) => email.email).filter(Boolean);

        return this.createValue(card.id, emails, emails.join('\n'), { lines: emails });
      }
      case 'addresses': {
        const addresses = (card.addresses ?? []).map(toAddressInput);
        const apartmentLabel = this.translate('address_apartment_short');
        const lines = addresses.map((address) => formatAddress(address, apartmentLabel)).filter(Boolean);

        return this.createValue(card.id, addresses, lines.join('\n'), { lines });
      }
      case 'description':
        return this.createValue(card.id, card.description ?? '', (card.description ?? '').trim());
      default:
        return this.createValue(card.id, card.name ?? '', (card.name ?? '').trim());
    }
  }

  private buildCustomField(key: string, title: string, translate: boolean): MergeField | null {
    const values = this.cards.reduce<Record<string, MergeValue>>((acc, card) => {
      acc[card.id] = this.buildCustomValue(card, key);

      return acc;
    }, {});
    if (Object.values(values).every((value) => value.isEmpty)) return null;

    const hasArrayValue = this.cards.some((card) => Array.isArray(this.rawCustomValue(card, key)));
    const hasLines = Object.values(values).some((value) => value.lines.length > 1);

    return {
      key,
      title,
      translate,
      kind: hasArrayValue ? 'multi' : 'single',
      render: hasArrayValue || hasLines ? 'list' : 'text',
      values,
    };
  }

  private buildCustomValue(card: ICardV2, key: string): MergeValue {
    const raw = this.rawCustomValue(card, key);
    if (Array.isArray(raw)) {
      const lines = raw.map((item) => this.stringifyValue(item)).filter(Boolean);

      return this.createValue(card.id, raw, lines.join('\n'), { lines });
    }

    if (this.isPlainObject(raw) && !isPriceFieldValue(raw)) {
      const lines = Object.entries(raw)
        .map(([entryKey, entryValue]) => {
          const text = this.stringifyValue(entryValue);

          return text ? `${entryKey}: ${text}` : '';
        })
        .filter(Boolean);

      return this.createValue(card.id, raw, lines.join('\n'), { lines });
    }

    return this.createValue(card.id, raw, this.stringifyValue(raw));
  }

  private createValue(
    cardId: string,
    raw: unknown,
    text: string,
    extra: { lines?: string[]; tag?: Tag | null; user?: MergeUser | null } = {},
  ): MergeValue {
    const trimmed = (text ?? '').trim();
    const lines = extra.lines ?? (trimmed ? trimmed.split('\n') : []);

    return {
      cardId,
      raw,
      text: trimmed,
      lines,
      isEmpty: !trimmed && !lines.length,
      tag: extra.tag ?? null,
      user: extra.user ?? null,
    };
  }

  private rawCustomValue(card: ICardV2, key: string): unknown {
    const customValue: unknown = card.custom_fields?.[key];
    if (customValue !== undefined && customValue !== null && customValue !== '') return customValue;

    const attributeValue: unknown = card.custom_attributes?.[key];

    return attributeValue ?? null;
  }

  private collectCustomKeys(): string[] {
    const keys = new Set<string>();
    this.cards.forEach((card) => {
      Object.keys(card.custom_fields ?? {}).forEach((key) => keys.add(key));
      Object.keys(card.custom_attributes ?? {}).forEach((key) => keys.add(key));
    });

    return [...keys];
  }

  private resolveSingleSource(field: MergeField, preferredCardId: string): string {
    if (!field.values[preferredCardId]?.isEmpty) return preferredCardId;

    const filled = this.cards.find((card) => !field.values[card.id]?.isEmpty);

    return filled?.id ?? preferredCardId;
  }

  private recomputeResults(): void {
    this.groups.forEach((group) => group.fields.forEach((field) => this.recomputeField(field)));
  }

  private recomputeField(field: MergeField): void {
    if (field.kind === 'single') {
      this.results[field.key] = field.values[this.singleSelection[field.key]] ?? null;
      return;
    }

    const lines: string[] = [];
    this.cards.forEach((card) => {
      if (!this.isSelected(field, card.id)) return;

      field.values[card.id]?.lines.forEach((line) => {
        if (!lines.includes(line)) lines.push(line);
      });
    });

    this.results[field.key] = this.createValue('', lines, lines.join('\n'), { lines });
  }

  private rawValueFor(field: MergeField, sourceCardIds: string[]): unknown {
    if (field.kind === 'single') return field.values[sourceCardIds[0]]?.raw ?? null;

    const raws = sourceCardIds.map((cardId) => field.values[cardId]?.raw).filter((raw) => raw !== null && raw !== undefined);
    if (raws.every((raw) => Array.isArray(raw))) return (raws as unknown[][]).flat();

    return raws.map((raw) => this.stringifyValue(raw)).filter(Boolean).join('\n');
  }

  private stringifyValue(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return this.translate(value ? 'yes' : 'no');
    if (Array.isArray(value)) {
      return value
        .map((item) => this.stringifyValue(item))
        .filter(Boolean)
        .join(', ');
    }
    if (isPriceFieldValue(value)) return formatPriceFieldValue(value);
    if (this.isPlainObject(value)) {
      return this.stringifyValue(value['name'] ?? value['title'] ?? value['label'] ?? value['value'] ?? '');
    }
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'bigint') return String(value);

    return '';
  }

  private translate(key: string, params?: Record<string, unknown>): string {
    const value: unknown = this.translateService.instant(key, params);

    return typeof value === 'string' ? value : key;
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  /** Board v3 columns expose `color`, legacy ones only `bg_color`. */
  private columnColor(column: IColumn | undefined): string | undefined {
    const color = (column as unknown as { color?: BoardColumnColor } | undefined)?.color;

    return color?.background || column?.bg_color || undefined;
  }

  private managerName(manager: IUser | null | undefined): string {
    if (!manager) return '';

    return formatFullName(manager.first_name, manager.last_name) || (manager.email ?? '');
  }

  private managerAvatarUrl(manager: IUser | null | undefined): string {
    if (!manager?.avatar_url) return DEFAULT_PROFILE_ICON;
    if (manager.avatar_url.startsWith('http') || manager.avatar_url.startsWith('/')) return manager.avatar_url;

    return `${environment.filesURL}/files/download?key=${manager.avatar_url}`;
  }
}
